import React, { useState } from 'react';
import { T, serif, fmt, Card, Boton, Campo, estiloInput, Aviso } from '../lib/ui';

const MEDIOS = [
  { valor: 'bus', texto: 'Bus' },
  { valor: 'colectivo', texto: 'Colectivo' },
  { valor: 'micro', texto: 'Micro' },
  { valor: 'taxi', texto: 'Taxi' },
];

const lineaVacia = () => ({
  fecha: new Date().toISOString().slice(0, 10),
  origen: '', destino: '', motivo: '', importe: '', medio: 'taxi',
});

/**
 * Planilla por gasto de movilidad.
 *
 * Replica el talonario de SICREA, pero valida el tope diario mientras
 * se escribe: es preferible avisar aquí que descubrirlo en el cierre.
 */
export function PlanillaMovilidad({ topeDiario, topes, onCambio }) {
  // El tope del dia en que se viajo, no el de hoy: la RMV cambia por
  // decreto y una planilla puede cruzar la fecha del cambio.
  const topeDe = (fecha) => {
    if (!topes?.length) return topeDiario;
    const dia = String(fecha || '').slice(0, 10);
    const v = topes.find((x) => dia >= x.desde);
    return (v || topes[topes.length - 1]).tope;
  };
  const [lineas, setLineas] = useState([lineaVacia()]);

  const actualizar = (i, campo, valor) => {
    const nuevas = lineas.map((l, idx) => (idx === i ? { ...l, [campo]: valor } : l));
    setLineas(nuevas);
    onCambio(nuevas.filter((l) => l.motivo && Number(l.importe) > 0).map((l) => ({
      ...l, importe: Number(l.importe),
    })));
  };

  const agregar = () => setLineas([...lineas, lineaVacia()]);
  const quitar = (i) => {
    const nuevas = lineas.filter((_, idx) => idx !== i);
    const finales = nuevas.length ? nuevas : [lineaVacia()];
    setLineas(finales);
    onCambio(finales.filter((l) => l.motivo && Number(l.importe) > 0).map((l) => ({
      ...l, importe: Number(l.importe),
    })));
  };

  // Acumulado por día, para poder marcar en rojo el que se pasa.
  const porFecha = {};
  for (const l of lineas) {
    const n = Number(l.importe) || 0;
    if (l.fecha && n > 0) porFecha[l.fecha] = (porFecha[l.fecha] || 0) + n;
  }
  const excedidos = Object.entries(porFecha).filter(([f, m]) => m > topeDe(f));
  const total = Object.values(porFecha).reduce((s, v) => s + v, 0);

  return (
    <>
      <Aviso tipo="info">
        Tope de {fmt.money(topeDiario)} por día y por trabajador. Es el 4% de
        la remuneración mínima vigente el día del viaje, artículo 37 inciso
        a1) de la Ley del Impuesto a la Renta. No es un tope por recibo:
        varios recibos del mismo día de la misma persona se suman. Lo que pase
        de ahí no se puede deducir.
      </Aviso>

      {excedidos.length > 0 && (
        <Aviso tipo="bad">
          {excedidos.map(([f, m]) => (
            <div key={f}>
              El {fmt.fecha(f)} suma {fmt.money(m)} y el tope de ese día es{' '}
              {fmt.money(topeDe(f))}.
            </div>
          ))}
        </Aviso>
      )}

      {lineas.map((l, i) => {
        const excede = porFecha[l.fecha] > topeDe(l.fecha);
        return (
          <Card key={i} style={{
            marginBottom: 12, padding: 14,
            borderLeft: `3px solid ${excede ? T.bad : T.border}`,
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 10,
            }}>
              <span style={{
                fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: '.05em',
              }}>
                DESPLAZAMIENTO {i + 1}
              </span>
              {lineas.length > 1 && (
                <button type="button" onClick={() => quitar(i)} style={{
                  background: 'none', border: 'none', color: T.bad,
                  fontSize: 12, cursor: 'pointer', padding: 0,
                }}>
                  Quitar
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12 }}>
              <Campo etiqueta="Día">
                <input type="date" value={l.fecha}
                       onChange={(e) => actualizar(i, 'fecha', e.target.value)}
                       style={estiloInput} />
              </Campo>
              <Campo etiqueta="Medio">
                <select value={l.medio} onChange={(e) => actualizar(i, 'medio', e.target.value)}
                        style={estiloInput}>
                  {MEDIOS.map((m) => <option key={m.valor} value={m.valor}>{m.texto}</option>)}
                </select>
              </Campo>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12 }}>
              <Campo etiqueta="De">
                <input value={l.origen} onChange={(e) => actualizar(i, 'origen', e.target.value)}
                       placeholder="Lurín" style={estiloInput} />
              </Campo>
              <Campo etiqueta="A o para">
                <input value={l.destino} onChange={(e) => actualizar(i, 'destino', e.target.value)}
                       placeholder="Callao" style={estiloInput} />
              </Campo>
            </div>

            <Campo etiqueta="Motivo"
                   ayuda="Obligatorio: sin motivo la planilla no sustenta el gasto.">
              <input value={l.motivo} onChange={(e) => actualizar(i, 'motivo', e.target.value)}
                     placeholder="Visita técnica a cliente" style={estiloInput} />
            </Campo>

            <Campo etiqueta="Importe">
              <input inputMode="decimal" value={l.importe}
                     onChange={(e) => actualizar(i, 'importe', e.target.value)}
                     style={{
                       ...estiloInput, fontWeight: 700,
                       borderColor: excede ? T.bad : T.border,
                     }} />
            </Campo>
          </Card>
        );
      })}

      <Boton type="button" variante="suave" onClick={agregar}
             style={{ width: '100%', marginBottom: 14 }}>
        Agregar otro desplazamiento
      </Boton>

      <Card style={{ background: T.warm, marginBottom: 16 }}>
        {Object.entries(porFecha).map(([f, m]) => (
          <div key={f} style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: 13, marginBottom: 4,
            color: m > topeDe(f) ? T.bad : T.text,
          }}>
            <span>{fmt.fecha(f)}</span>
            <span style={{ fontWeight: 600 }}>
              {fmt.money(m)} / {fmt.money(topeDe(f))}
            </span>
          </div>
        ))}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          borderTop: `1px solid ${T.border}`, marginTop: 8, paddingTop: 8,
        }}>
          <span style={{ fontWeight: 600 }}>Total de la planilla</span>
          <span style={{ fontFamily: serif, fontSize: 20, fontWeight: 600, color: T.navy }}>
            {fmt.money(total)}
          </span>
        </div>
      </Card>
    </>
  );
}

/**
 * Recibo AD: el talonario interno de SICREA.
 * No es comprobante de pago, y la pantalla lo dice para que nadie lo
 * use pensando que sustenta un gasto deducible.
 */
export function ReciboAd({ datos, onCambio, padron }) {
  // ── Por qué no se manda `{ ...datos, [k]: v }` ────────────────────
  //
  // `datos` es la copia del formulario tal como estaba EN ESE RENDER.
  // Al mandar la copia entera, dos llamadas seguidas dentro del mismo
  // evento se pisan: la segunda reenvía el valor viejo del campo que
  // acababa de cambiar la primera y lo revierte.
  //
  // Eso es lo que pasaba en "Quién recibe": su onChange llamaba a set()
  // dos veces —una para el nombre y otra para el id del padrón— así que
  // cada tecla se borraba sola y el campo parecía no aceptar nada. No
  // era el desplegable: era que el valor no se quedaba.
  //
  // Mandando solo el campo que cambió, el padre lo mezcla con el estado
  // fresco y las dos llamadas conviven.
  const set = (k, v) => onCambio({ [k]: v });
  /** Varios campos de un golpe, para no depender del orden. */
  const setVarios = (campos) => onCambio(campos);

  return (
    <>
      <Aviso tipo="warn">
        El recibo interno no es un comprobante de pago. Sirve para dejar
        constancia de una entrega o una devolución. Contabilidad decidirá
        si el gasto es deducible.
      </Aviso>

      <Card style={{ marginBottom: 16 }}>
        {/* Dos talonarios distintos: AD para administración, OP para obra. */}
        <Campo etiqueta="Serie del talonario">
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { valor: 'AD', texto: 'AD · Administrativo', ayuda: 'Oficina y administración' },
              { valor: 'OP', texto: 'OP · Operativo', ayuda: 'Obra, campo y servicio' },
            ].map((op) => {
              const activa = (datos.reciboSerie || 'AD') === op.valor;
              return (
                <button key={op.valor} type="button"
                        onClick={() => set('reciboSerie', op.valor)}
                        style={{
                          flex: 1, textAlign: 'left', cursor: 'pointer',
                          padding: '10px 12px', borderRadius: 8,
                          border: `1px solid ${activa ? T.navy : T.border}`,
                          background: activa ? T.navy : T.paper,
                          color: activa ? '#fff' : T.text,
                        }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{op.texto}</div>
                  <div style={{
                    fontSize: 11, marginTop: 2,
                    color: activa ? T.brass : T.muted,
                  }}>
                    {op.ayuda}
                  </div>
                </button>
              );
            })}
          </div>
        </Campo>

        <Campo etiqueta="N° del talonario de papel"
               ayuda="Opcional. El número de la plataforma se asigna solo al registrar.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontFamily: 'monospace', fontSize: 15, fontWeight: 700,
              color: T.navy, background: T.warm,
              padding: '10px 12px', borderRadius: 8,
            }}>
              {datos.reciboSerie || 'AD'}-
            </span>
            <input value={datos.numeroDocumento || ''}
                   onChange={(e) => set('numeroDocumento', e.target.value)}
                   placeholder="000075" style={{ ...estiloInput, flex: 1 }} />
          </div>
        </Campo>

        <Campo
          etiqueta="Quién recibe"
          ayuda={(padron || []).length > 0
            ? `Elige del padrón (${padron.length} personas) para que el DNI y el `
              + 'nombre salgan bien en el impreso.'
            : 'El padrón no cargó: escribe los apellidos y nombres a mano. '
              + 'El DNI habrá que ponerlo al imprimir.'}
        >
          <input
            list="padron-personal"
            value={datos.proveedorRazon || ''}
            onChange={(e) => {
              const v = e.target.value;
              const p = (padron || []).find((x) => x.nombre === v);
              // Los dos campos en una sola llamada. Antes iban en dos y
              // la segunda revertía a la primera.
              //
              // El DNI NO se copia aquí: vive en el padrón y se lee de
              // ahí al imprimir. Guardarlo dos veces invita a que se
              // desincronicen. Además el campo de proveedor espera un
              // RUC de 11 dígitos, no un DNI de 8.
              setVarios({ proveedorRazon: v, personalId: p ? p.id : null });
            }}
            placeholder="Apellidos y nombres"
            style={estiloInput}
          />
          <datalist id="padron-personal">
            {(padron || []).map((p) => (
              <option key={p.id} value={p.nombre}>{`DNI ${p.dni}`}</option>
            ))}
          </datalist>
        </Campo>

        {/* La fecha faltaba: el recibo se guardaba siempre con la del
            dia de captura, y recien se podia corregir despues de
            generado. Un recibo que se llena el lunes por una entrega
            del viernes salia con la fecha equivocada. */}
        <Campo etiqueta="Fecha del recibo"
               ayuda="El día de la entrega, no el día en que se registra.">
          <input type="date" value={datos.fecha || ''}
                 onChange={(e) => set('fecha', e.target.value)}
                 style={estiloInput} />
        </Campo>

        <Campo etiqueta="Recibí de SICREA S.A.C. la cantidad de">
          <input inputMode="decimal" value={datos.total || ''}
                 onChange={(e) => set('total', e.target.value)}
                 style={{ ...estiloInput, fontWeight: 700 }} />
        </Campo>

        <Campo etiqueta="Concepto">
          <input value={datos.concepto || ''}
                 onChange={(e) => set('concepto', e.target.value)}
                 placeholder="Adelanto por servicio de soldadura" style={estiloInput} />
        </Campo>

        {/* Aquí había un segundo campo, "Nombre de quien recibe", atado
            al MISMO dato que "Quién recibe" de arriba. Dos casillas para
            lo mismo: al escribir en esta se perdía el vínculo con el
            padrón que había hecho la de arriba, y no había manera de
            saber cuál de las dos valía. Se quedó una sola, la que sí
            resuelve el DNI desde el padrón. */}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12 }}>
          <Campo etiqueta="DNI">
            <input value={datos.dni || ''} inputMode="numeric" maxLength={8}
                   onChange={(e) => set('dni', e.target.value.replace(/\D/g, ''))}
                   style={estiloInput} />
          </Campo>
          <Campo etiqueta="RUC" ayuda="Si tiene.">
            <input value={datos.proveedorRuc || ''} inputMode="numeric" maxLength={11}
                   onChange={(e) => set('proveedorRuc', e.target.value.replace(/\D/g, ''))}
                   style={estiloInput} />
          </Campo>
        </div>

        <Campo etiqueta="Forma de pago">
          <select value={datos.medioPago || 'efectivo'}
                  onChange={(e) => set('medioPago', e.target.value)} style={estiloInput}>
            <option value="efectivo">Efectivo</option>
            <option value="transferencia">Transferencia</option>
          </select>
        </Campo>
      </Card>
    </>
  );
}
